// src/lib/notion-api.js
const BASE_URL = import.meta.env.DEV ? 'http://localhost:3000/api' : '/api';

export async function fetchDashboardSummary() {
  const res = await fetch(`${BASE_URL}/notion-dashboard`);
  if (!res.ok) throw new Error(`Dashboard API error: ${res.status}`);
  return res.json();
}

export async function fetchHealthPosts(params = {}) {
  const res = await fetch(`${BASE_URL}/notion-health?${buildQuery(params)}`);
  if (!res.ok) throw new Error(`Health API error: ${res.status}`);
  return res.json();
}

export async function fetchSportsPosts(params = {}) {
  const res = await fetch(`${BASE_URL}/notion-sports?${buildQuery(params)}`);
  if (!res.ok) throw new Error(`Sports API error: ${res.status}`);
  return res.json();
}

export async function fetchTodos(params = {}) {
  const res = await fetch(`${BASE_URL}/notion-todos?${buildQuery(params)}`);
  if (!res.ok) throw new Error(`Todos API error: ${res.status}`);
  return res.json();
}

export async function fetchSchema(db) {
  const res = await fetch(`${BASE_URL}/notion-schema?db=${db}`);
  if (!res.ok) throw new Error(`Schema API error: ${res.status}`);
  return res.json();
}

export async function updateTodoStatus(pageId, status) {
  const res = await fetch(`${BASE_URL}/notion-update-todo`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pageId, status }),
  });
  if (!res.ok) throw new Error(`Update error: ${res.status}`);
  return res.json();
}

export async function fetchRevenue(params = {}) {
  const res = await fetch(`${BASE_URL}/notion-revenue?${buildQuery(params)}`);
  if (!res.ok) throw new Error(`Revenue API error: ${res.status}`);
  return res.json();
}

export async function fetchExpenses(params = {}) {
  const res = await fetch(`${BASE_URL}/notion-expense?${buildQuery(params)}`);
  if (!res.ok) throw new Error(`Expense API error: ${res.status}`);
  return res.json();
}

export async function addExpense(data) {
  const res = await fetch(`${BASE_URL}/notion-add-expense`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    let detail = '';
    try { const j = await res.json(); detail = j.detail || j.error || ''; } catch {}
    throw new Error(`Add expense error: ${res.status}${detail ? ` — ${detail}` : ''}`);
  }
  return res.json();
}

export async function fetchTrendFeed() {
  const res = await fetch(`${BASE_URL}/trend-feed`);
  if (!res.ok) throw new Error(`Trend feed error: ${res.status}`);
  return res.json();
}

export async function fetchSettlement(month) {
  const query = month ? `?month=${month}` : '';
  const res = await fetch(`${BASE_URL}/naver-settlement${query}`);
  if (!res.ok) throw new Error(`Settlement API error: ${res.status}`);
  return res.json();
}

/** 에이전트: 개별 할일 처리 */
export async function agentProcess(task, category, chatroom, priority, memo) {
  const res = await fetch(`${BASE_URL}/agent-process`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ task, category, chatroom, priority, memo }),
  });
  if (!res.ok) throw new Error(`Agent error: ${res.status}`);
  const data = await res.json();
  if (data.usage) trackTokenUsage('agent-process', data.usage);
  return data;
}

/** 에이전트: 목표 → 업무 분해 */
export async function agentPlan(goal) {
  const res = await fetch(`${BASE_URL}/agent-plan`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ goal }),
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    throw new Error(`Agent plan error: ${res.status} ${errText.slice(0, 200)}`);
  }
  const data = await res.json();
  if (data.usage) trackTokenUsage('agent-plan', data.usage);

  // plan 정규화: API가 { success, plan, usage } 형태로 옴
  let plan = data.plan || data;

  // plan이 문자열이면 JSON 파싱 시도
  if (typeof plan === 'string') {
    try {
      plan = JSON.parse(plan.replace(/```json\s?/g, '').replace(/```/g, '').trim());
    } catch {
      const jsonMatch = plan.match(/\{[\s\S]*\}/);
      if (jsonMatch) try { plan = JSON.parse(jsonMatch[0]); } catch {}
    }
  }

  // tasks 배열 정규화 (task/title 필드 통일)
  if (plan?.tasks) {
    plan.tasks = plan.tasks.map(t => ({
      ...t,
      task: t.task || t.title || '작업',
      action: t.action || t.content || t.text || '',
    }));
  }

  return plan;
}

/** 에이전트 체인: 여러 에이전트 순차 협업 */
export async function agentChain(command, context, history) {
  const res = await fetch(`${BASE_URL}/agent-chain`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ command, context, history }),
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    throw new Error(`Agent chain error: ${res.status} ${errText.slice(0, 200)}`);
  }
  const data = await res.json();
  if (data.usage) trackTokenUsage('agent-chain', data.usage);
  return data.result || data;
}

/** 아이디어 회의 세션 목록 조회 */
export async function fetchBrainstormSessions() {
  const res = await fetch(`${BASE_URL}/brainstorm-sessions`);
  if (!res.ok) throw new Error(`Sessions fetch error: ${res.status}`);
  return res.json();
}

/** 아이디어 회의 세션 저장 */
export async function saveBrainstormSession(data) {
  const res = await fetch(`${BASE_URL}/brainstorm-sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Session save error: ${res.status}`);
  return res.json();
}

/** 아이디어 회의 세션 삭제 (Notion 아카이브) */
export async function deleteBrainstormSession(pageId) {
  const res = await fetch(`${BASE_URL}/brainstorm-sessions`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pageId }),
  });
  if (!res.ok) throw new Error(`Session delete error: ${res.status}`);
  return res.json();
}

/** 아이디어 회의 개별 세션 전체 데이터 조회 (보류 이어서 / 회의록) */
export async function fetchBrainstormSession(pageId) {
  const res = await fetch(`${BASE_URL}/brainstorm-session?pageId=${encodeURIComponent(pageId)}`);
  if (!res.ok) throw new Error(`Session detail error: ${res.status}`);
  return res.json();
}

/** 아이디어 회의: 고민/아이디어를 관련 에이전트가 다각도 분석 */
export async function agentBrainstorm(topic, history, signal) {
  const res = await fetch(`${BASE_URL}/agent-brainstorm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ topic, history }),
    signal,
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    throw new Error(`Brainstorm error: ${res.status} ${errText.slice(0, 200)}`);
  }
  const data = await res.json();
  if (data.usage) trackTokenUsage('agent-brainstorm', data.usage);
  return data.result || data;
}

/** 토큰 사용량 추적 (localStorage) */
function trackTokenUsage(source, usage) {
  try {
    const key = 'caio_token_usage';
    const stored = JSON.parse(localStorage.getItem(key) || '{"history":[],"totals":{"input":0,"output":0,"cost":0}}');
    const inputCost = (usage.input_tokens || 0) * 3.0 / 1_000_000;
    const outputCost = (usage.output_tokens || 0) * 15.0 / 1_000_000;
    const cost = inputCost + outputCost;
    stored.history.push({
      source,
      input: usage.input_tokens || 0,
      output: usage.output_tokens || 0,
      cost,
      time: new Date().toISOString(),
    });
    // 최근 100건만 유지
    if (stored.history.length > 100) stored.history = stored.history.slice(-100);
    stored.totals.input += (usage.input_tokens || 0);
    stored.totals.output += (usage.output_tokens || 0);
    stored.totals.cost += cost;
    localStorage.setItem(key, JSON.stringify(stored));
    // 실시간 업데이트 — 같은 탭의 TokenUsage 컴포넌트에 즉시 반영
    window.dispatchEvent(new CustomEvent('caio-token-update'));
  } catch (e) { /* localStorage 실패 시 무시 */ }
}

/** 토큰 사용량 조회 */
export function getTokenUsage() {
  try {
    return JSON.parse(localStorage.getItem('caio_token_usage') || '{"history":[],"totals":{"input":0,"output":0,"cost":0}}');
  } catch { return { history: [], totals: { input: 0, output: 0, cost: 0 } }; }
}

function buildQuery(params) {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') sp.set(key, value);
  }
  return sp.toString();
}
